/**
 * The room detail page (`/property/:id`), built to the website draft's "3d"
 * screen ("Zimmeransicht").
 *
 * Presentation only — every figure is passed in from `pages/PropertyDetail`,
 * which owns the queries, the favourite mutation and the apply flow. Nothing
 * here invents a number: where the draft shows a field the schema has no column
 * for (room size, floor, nearby transit times), the block is simply not
 * rendered rather than filled with a plausible-looking value. This is a public
 * rental listing; a made-up "14 m²" is a misrepresentation, not a placeholder.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Check,
  ChevronRight,
  Heart,
  MapPin,
  Share2,
  ImageIcon,
} from "lucide-react";
import { SmoothImage } from "@/components/ui/smooth-image";
import {
  INK,
  MUTED,
  MUTED_SOFT,
  SAND,
  LINE,
  LINE_STRONG,
  IMAGE_BG,
  EASE,
} from "@/lib/marketing-theme";

type Lang = { de: boolean };

const H2 =
  "font-display text-[1.6rem] font-semibold leading-[1.1] sm:text-[1.9rem]";
const H2_STYLE = { letterSpacing: "-0.03em", color: INK } as const;

function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: delay * 0.07, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Breadcrumb + title                                                  */
/* ------------------------------------------------------------------ */

export function RoomHeader({
  de,
  title,
  city,
  subtitle,
  isFavorited,
  onFavorite,
  onShare,
  shareCopied,
}: Lang & {
  title: string;
  city: string;
  subtitle: string;
  isFavorited: boolean;
  onFavorite: () => void;
  onShare: () => void;
  shareCopied: boolean;
}) {
  const crumb =
    "text-[13.5px] transition-colors hover:opacity-70 whitespace-nowrap";
  return (
    <div className="flex flex-col gap-6">
      <nav
        aria-label={de ? "Brotkrumen" : "Breadcrumb"}
        className="flex flex-wrap items-center gap-x-2 gap-y-1"
      >
        <Link to="/rooms" className={crumb} style={{ color: MUTED }}>
          {de ? "WG-Zimmer" : "Rooms"}
        </Link>
        {city && (
          <>
            <ChevronRight
              className="h-3.5 w-3.5 shrink-0"
              style={{ color: MUTED_SOFT }}
              aria-hidden="true"
            />
            <Link
              to={`/rooms/${encodeURIComponent(city.toLowerCase())}`}
              className={crumb}
              style={{ color: MUTED }}
            >
              {city}
            </Link>
          </>
        )}
        <ChevronRight
          className="h-3.5 w-3.5 shrink-0"
          style={{ color: MUTED_SOFT }}
          aria-hidden="true"
        />
        <span
          className="text-[13.5px] font-semibold"
          style={{ color: INK }}
          aria-current="page"
        >
          {title}
        </span>
      </nav>

      <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between md:gap-10">
        <div className="min-w-0">
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="font-display text-[2rem] font-semibold leading-[1.04] sm:text-[2.6rem] lg:text-[3rem]"
            style={{ letterSpacing: "-0.038em", color: INK }}
          >
            {title}
          </motion.h1>
          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
              className="mt-2.5 text-[16px] sm:text-[17.5px]"
              style={{ color: MUTED }}
            >
              {subtitle}
            </motion.p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <button
            type="button"
            onClick={onShare}
            className="inline-flex h-11 items-center gap-2 rounded-full border bg-white px-5 text-[14.5px] font-medium transition-transform duration-300 hover:-translate-y-0.5"
            style={{ borderColor: LINE_STRONG, color: INK }}
          >
            {shareCopied ? (
              <Check className="h-4 w-4" strokeWidth={2} />
            ) : (
              <Share2 className="h-4 w-4" strokeWidth={1.8} />
            )}
            {shareCopied
              ? de
                ? "Kopiert"
                : "Copied"
              : de
                ? "Teilen"
                : "Share"}
          </button>
          <motion.button
            type="button"
            onClick={onFavorite}
            aria-pressed={isFavorited}
            whileTap={{ scale: 0.9 }}
            whileHover={{ y: -2 }}
            className="inline-flex h-11 items-center gap-2 rounded-full border bg-white px-5 text-[14.5px] font-medium"
            style={{ borderColor: LINE_STRONG, color: INK }}
          >
            <Heart
              className="h-4 w-4"
              strokeWidth={1.8}
              fill={isFavorited ? INK : "none"}
            />
            {de ? "Merken" : "Save"}
          </motion.button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Gallery — one large frame with four thumbnails beside it            */
/* ------------------------------------------------------------------ */

export function RoomGallery({
  de,
  images,
  alt,
  onOpen,
}: Lang & { images: string[]; alt: string; onOpen: (index: number) => void }) {
  if (images.length === 0) return null;
  const [lead, ...rest] = images;
  const thumbs = rest.slice(0, 4);

  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
      className="relative grid gap-2.5 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr] lg:grid-rows-2"
    >
      <button
        type="button"
        onClick={() => onOpen(0)}
        className="group relative block h-[240px] overflow-hidden rounded-[14px] sm:h-[320px] lg:col-start-1 lg:row-span-2 lg:h-full lg:min-h-[470px]"
        style={{ background: IMAGE_BG }}
      >
        <SmoothImage
          src={lead}
          alt={alt}
          wrapperClassName="absolute inset-0 h-full w-full"
          className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
        />
      </button>

      {thumbs.map((src, i) => (
        <button
          key={`${src}-${i}`}
          type="button"
          onClick={() => onOpen(i + 1)}
          className="group relative hidden h-[230px] overflow-hidden rounded-[14px] sm:block"
          style={{ background: IMAGE_BG }}
        >
          <SmoothImage
            src={src}
            alt=""
            wrapperClassName="absolute inset-0 h-full w-full"
            className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
          />
        </button>
      ))}

      <button
        type="button"
        onClick={() => onOpen(0)}
        className="absolute bottom-4 right-4 inline-flex h-10 items-center gap-2 rounded-full bg-white px-4 text-[13.5px] font-medium shadow-[0_10px_28px_rgba(9,46,44,0.18)] transition-transform duration-300 hover:-translate-y-0.5"
        style={{ color: INK }}
      >
        <ImageIcon className="h-3.5 w-3.5" strokeWidth={1.8} />
        {de ? `Alle ${images.length} Fotos` : `All ${images.length} photos`}
      </button>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Facts strip — only the facts the schema can actually back           */
/* ------------------------------------------------------------------ */

/**
 * One fact in the strip. `value` is the plain-text form; `node` replaces it
 * for a fact the schema can back with more than a string — today only the
 * flatmate row (COL-530), which is a set of people rather than a number.
 * `value` stays required either way: it is the key, and the accessible text.
 */
export type RoomFact = {
  label: string;
  value: string;
  node?: ReactNode;
};

export function RoomFacts({ facts }: { facts: RoomFact[] }) {
  if (facts.length === 0) return null;
  return (
    <Reveal>
      <dl
        className="grid gap-x-10 gap-y-6 border-b pb-8 sm:grid-cols-3"
        style={{ borderColor: LINE_STRONG }}
      >
        {facts.map((f) => (
          <div key={f.label} className="flex flex-col gap-1">
            <dt
              className="text-[13px] font-medium"
              style={{ color: MUTED_SOFT }}
            >
              {f.label}
            </dt>
            <dd
              className="text-[19px] font-semibold"
              style={{ letterSpacing: "-0.02em", color: INK }}
            >
              {f.node ?? f.value}
            </dd>
          </div>
        ))}
      </dl>
    </Reveal>
  );
}

/* ------------------------------------------------------------------ */
/* Prose + checklist blocks                                            */
/* ------------------------------------------------------------------ */

export function RoomSection({
  title,
  children,
  divider = true,
  delay = 0,
}: {
  title: string;
  children: React.ReactNode;
  divider?: boolean;
  delay?: number;
}) {
  return (
    <Reveal delay={delay}>
      <section
        className={divider ? "border-b py-9" : "py-9"}
        style={divider ? { borderColor: LINE_STRONG } : undefined}
      >
        <h2 className={H2} style={H2_STYLE}>
          {title}
        </h2>
        <div className="mt-5">{children}</div>
      </section>
    </Reveal>
  );
}

export function RoomFeatureList({ items }: { items: string[] }) {
  return (
    <ul className="grid gap-x-10 gap-y-3.5 sm:grid-cols-2">
      {items.map((item, i) => (
        <motion.li
          key={`${item}-${i}`}
          initial={{ opacity: 0, x: -8 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.45, ease: EASE, delay: i * 0.05 }}
          className="flex items-start gap-3 text-[15.5px]"
          style={{ color: INK }}
        >
          <Check
            className="mt-[3px] h-4 w-4 shrink-0"
            strokeWidth={2.4}
            style={{ color: INK }}
            aria-hidden="true"
          />
          {item}
        </motion.li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------------ */
/* "So läuft's ab"                                                     */
/* ------------------------------------------------------------------ */

export function RoomSteps({ de }: Lang) {
  const steps = de
    ? [
        {
          no: "01",
          name: "Bewerben",
          body: "Kurzprofil in der App, dauert 5 Minuten.",
        },
        {
          no: "02",
          name: "Prüfung",
          body: "Wir schauen uns dein Profil an und melden uns innerhalb von 24 Stunden.",
        },
        {
          no: "03",
          name: "Vertrag",
          body: "Einzelmietvertrag digital unterschreiben.",
        },
        {
          no: "04",
          name: "Einziehen",
          body: "Schlüssel abholen, Übergabe in der App.",
        },
      ]
    : [
        {
          no: "01",
          name: "Apply",
          body: "A short profile in the app, five minutes.",
        },
        {
          no: "02",
          name: "Review",
          body: "We look at your profile and get back to you within 24 hours.",
        },
        { no: "03", name: "Contract", body: "Sign your own lease digitally." },
        {
          no: "04",
          name: "Move in",
          body: "Collect the keys, handover in the app.",
        },
      ];

  return (
    <div className="grid gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
      {steps.map((s, i) => (
        <motion.div
          key={s.no}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5, ease: EASE, delay: i * 0.08 }}
          className="flex flex-col gap-2"
        >
          <motion.span
            aria-hidden="true"
            className="block h-[2px] w-full origin-left"
            style={{ background: i === 0 ? INK : LINE_STRONG }}
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, amount: 0.8 }}
            transition={{ duration: 0.6, ease: EASE, delay: i * 0.08 }}
          />
          <span
            className="pt-3 text-[12.5px] font-bold tracking-[0.14em]"
            style={{ color: MUTED_SOFT }}
          >
            {s.no}
          </span>
          <span
            className="text-[17px] font-semibold"
            style={{ letterSpacing: "-0.02em", color: INK }}
          >
            {s.name}
          </span>
          <span className="text-[15px] leading-[1.55]" style={{ color: MUTED }}>
            {s.body}
          </span>
        </motion.div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Booking card                                                        */
/* ------------------------------------------------------------------ */

export function RoomBookingCard({
  de,
  price,
  moveInLabel,
  moveInValue,
  rows,
  applyHref,
  applyLabel,
  applyDisabled,
}: Lang & {
  price: string;
  moveInLabel: string;
  moveInValue: string;
  rows: { label: string; value: string }[];
  applyHref: string;
  applyLabel: string;
  applyDisabled: boolean;
}) {
  return (
    <div className="flex flex-col gap-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
        className="rounded-[18px] border bg-white p-6 shadow-[0_18px_44px_rgba(9,46,44,0.08)] sm:p-7"
        style={{ borderColor: LINE }}
      >
        <div className="flex items-baseline gap-2">
          <span
            className="font-display text-[32px] font-semibold"
            style={{ letterSpacing: "-0.035em", color: INK }}
          >
            {price}
          </span>
          <span className="text-[15px]" style={{ color: MUTED }}>
            {de ? "/ Monat, warm" : "/ month, all in"}
          </span>
        </div>

        <div
          className="mt-5 grid grid-cols-2 overflow-hidden rounded-[12px] border"
          style={{ borderColor: LINE_STRONG }}
        >
          <div className="p-4">
            <span
              className="block text-[12.5px] font-medium"
              style={{ color: MUTED_SOFT }}
            >
              {de ? "Einzug" : "Move-in"}
            </span>
            <span
              className="mt-1 block text-[15px] font-semibold"
              style={{ color: INK }}
            >
              {moveInValue}
            </span>
          </div>
          <div className="border-l p-4" style={{ borderColor: LINE_STRONG }}>
            <span
              className="block text-[12.5px] font-medium"
              style={{ color: MUTED_SOFT }}
            >
              {de ? "Status" : "Status"}
            </span>
            <span
              className="mt-1 block text-[15px] font-semibold"
              style={{ color: INK }}
            >
              {moveInLabel}
            </span>
          </div>
        </div>

        {applyDisabled ? (
          <span
            className="mt-5 inline-flex h-[52px] w-full items-center justify-center rounded-full text-[15.5px] font-semibold"
            style={{ background: SAND, color: MUTED }}
          >
            {applyLabel}
          </span>
        ) : (
          <Link
            to={applyHref}
            className="group mt-5 inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-full text-[15.5px] font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
            style={{ background: INK }}
          >
            {applyLabel}
            <ArrowRight
              className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
              strokeWidth={2.4}
            />
          </Link>
        )}

        <dl className="mt-6 flex flex-col">
          {rows.map((r, i) => (
            <div
              key={r.label}
              className="flex items-center justify-between gap-4 py-3"
              style={i > 0 ? { borderTop: `1px solid ${LINE}` } : undefined}
            >
              <dt className="text-[14.5px]" style={{ color: MUTED }}>
                {r.label}
              </dt>
              <dd
                className="text-[14.5px] font-semibold"
                style={{ color: INK }}
              >
                {r.value}
              </dd>
            </div>
          ))}
        </dl>

        <p className="mt-4 text-[13px] leading-[1.55]" style={{ color: MUTED }}>
          {de
            ? "Einzelmietvertrag direkt mit coliviq. Keine Untermiete, keine versteckten Kosten."
            : "Your own lease, directly with coliviq. No subletting, no hidden costs."}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.3, ease: EASE }}
      >
        <Link
          to="/contact"
          className="group flex items-center gap-3.5 rounded-[14px] border p-5"
          style={{ background: SAND, borderColor: LINE }}
        >
          <div className="min-w-0">
            <span
              className="block text-[15px] font-semibold"
              style={{ color: INK }}
            >
              {de ? "Fragen zum Zimmer?" : "Questions about the room?"}
            </span>
            <span
              className="mt-0.5 block text-[13.5px] underline-offset-4 group-hover:underline"
              style={{ color: MUTED }}
            >
              {de
                ? "Wir antworten meist am selben Tag."
                : "We usually reply the same day."}
            </span>
          </div>
        </Link>
      </motion.div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* "Weitere Zimmer in dieser WG."                                      */
/* ------------------------------------------------------------------ */

export type SiblingRoom = {
  id: number;
  title: string;
  image: string | null;
  price: string;
  meta: string;
  badge: string;
  available: boolean;
};

export function RoomSiblings({
  de,
  rooms,
  allHref,
}: Lang & { rooms: SiblingRoom[]; allHref: string }) {
  if (rooms.length === 0) return null;
  return (
    <section
      className="border-t px-5 py-20 sm:px-8 lg:px-14"
      style={{ background: SAND, borderColor: LINE }}
    >
      <div className="mx-auto max-w-[1328px]">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h2 className={H2} style={H2_STYLE}>
              {de ? "Weitere Zimmer in dieser WG." : "More rooms in this flat."}
            </h2>
            <Link
              to={allHref}
              className="group inline-flex items-center gap-2 text-[15px] font-semibold"
              style={{ color: INK }}
            >
              {de ? "Alle Zimmer" : "All rooms"}
              <ArrowRight
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5"
                strokeWidth={2.4}
              />
            </Link>
          </div>
        </Reveal>

        <div className="mt-9 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {rooms.map((r, i) => (
            <Reveal key={r.id} delay={i}>
              <Link
                to={`/property/${r.id}`}
                className="group/card flex h-full flex-col gap-3.5"
              >
                <div
                  className="relative h-[230px] overflow-hidden rounded-[14px]"
                  style={{ background: IMAGE_BG }}
                >
                  {r.image && (
                    <SmoothImage
                      src={r.image}
                      alt={r.title}
                      wrapperClassName="absolute inset-0 h-full w-full"
                      className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover/card:scale-[1.04]"
                    />
                  )}
                  <span
                    className="absolute left-4 top-4 inline-flex h-8 items-center rounded-full px-3.5 text-[13px] font-semibold"
                    style={
                      r.available
                        ? { background: "#FFFFFF", color: INK }
                        : { background: SAND, color: MUTED }
                    }
                  >
                    {r.badge}
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-4">
                  <span
                    className="text-[17px] font-semibold"
                    style={{ letterSpacing: "-0.02em", color: INK }}
                  >
                    {r.title}
                  </span>
                  <span
                    className="shrink-0 text-[15.5px] font-semibold"
                    style={{ color: INK }}
                  >
                    {r.price}
                  </span>
                </div>
                {r.meta && (
                  <span className="text-[14.5px]" style={{ color: MUTED }}>
                    {r.meta}
                  </span>
                )}
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Location                                                            */
/* ------------------------------------------------------------------ */

export function RoomLocation({
  de,
  note,
  children,
}: Lang & { note: string; children: React.ReactNode }) {
  return (
    <>
      {children ?? (
        <div
          className="relative h-[300px] overflow-hidden rounded-[14px] border sm:h-[360px]"
          style={{ background: IMAGE_BG, borderColor: LINE }}
        >
          <span
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center"
            style={{ color: MUTED }}
          >
            <MapPin className="h-6 w-6" strokeWidth={1.4} />
            <span className="text-[14px]">
              {de ? "Karte nicht verfügbar" : "Map unavailable"}
            </span>
          </span>
        </div>
      )}
      <p className="mt-4 text-[14px]" style={{ color: MUTED }}>
        {note}
      </p>
    </>
  );
}
